import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Users, Wallet, FileBarChart2, IndianRupee, ShieldAlert, CalendarRange, Building2, ChevronDown, TrendingUp } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useBranch } from "@/context/BranchContext";
import { useNavigate } from "react-router-dom";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/collections", label: "Collections", icon: Wallet, testId: "nav-collections" },
  { to: "/delivery-boys", label: "Delivery Boys", icon: Users, testId: "nav-delivery-boys" },
  { to: "/profit", label: "Profit", icon: TrendingUp, testId: "nav-profit" },
  { to: "/weekly", label: "Weekly", icon: CalendarRange, testId: "nav-weekly" },
  { to: "/min-guarantee", label: "Min Guarantee", icon: ShieldAlert, testId: "nav-min-guarantee" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, testId: "nav-reports" },
];

export const Layout = () => {
  const { branches, branchId, setBranchId, activeBranch } = useBranch();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-700 text-white grid place-items-center">
              <IndianRupee className="h-5 w-5" strokeWidth={2.25} />
            </div>
            <div>
              <div className="font-display text-lg font-semibold leading-none">Cash Collect</div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-0.5">Daily Ledger</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Branch switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-lg border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid="branch-switcher"
                >
                  <Building2 className="h-4 w-4 mr-1.5" />
                  <span className="font-medium">{activeBranch?.name || "Select branch"}</span>
                  <ChevronDown className="h-3.5 w-3.5 ml-1.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-56">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Switch Branch
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={branchId} onValueChange={setBranchId}>
                  {branches.map((b) => (
                    <DropdownMenuRadioItem
                      key={b.id}
                      value={b.id}
                      data-testid={`branch-option-${b.name}`}
                    >
                      {b.name}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => navigate("/branches")}
                  data-testid="manage-branches-btn"
                >
                  <Users className="h-3.5 w-3.5 mr-1.5" /> Manage branches
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
              {nav.map(({ to, label, icon: Icon, testId }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  data-testid={testId}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-emerald-700 text-white"
                        : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
        {/* mobile nav */}
        <div className="md:hidden border-t border-zinc-200 overflow-x-auto">
          <nav className="flex items-center gap-1 px-4 py-2 min-w-max">
            {nav.map(({ to, label, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={`${testId}-mobile`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${
                    isActive ? "bg-emerald-700 text-white" : "text-zinc-600"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default Layout;
